import React, { useEffect, useState } from "react";
import { Alert } from "@material-ui/lab";
import { getServiceNameWithNickname } from "../../../utils/getServiceNameWithNickname";
import PageSpinner from "../../elements/spinner/PageSpinner";
import { NavLink } from "react-router-dom";
import DialogModal from "../../elements/dialogModal/DialogModal";
import TwoFa from "../../elements/twoFa/TwoFa";
import axios from "axios";
import DefaultButton from "../../styles/materialButtonStyle";
import { TextField } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import MaterialCheckbox from "../../styles/materialCheckbox";
import {
  StyledCol,
  StyledScrollTable,
  StyledTable,
  StyledTableBody,
  StyledTableHead,
  StyledTableRow
} from "../../styles/styledTable";
import { StyledCheckbox, StyledFormGrid } from "../../styles/styledTabsForm";
import { StyledApplyToItem, StyledApplyToWrapper } from "../../styles/styledApplyTo";
import userAuthenticationConfig from "../../../utils/userAuthenticationConfig";

const DefaultFeeList = ({ fees, notification, setNotification, fetchDefaultFees }) => {
  const { t } = useTranslation("defaultFeePage");

  const [open, setOpen] = useState(false);
  const [error, setError] = useState({});

  const [checked, setChecked] = useState([]);
  const [massEditingData, setMassEditingData] = useState({
    percent: 0,
    constant: 0,
    min: 0,
    max: 0
  });

  const inputType = {
    percent: "percent",
    constant: "constant",
    min: "min",
    max: "max"
  };

  const [currentType, setCurrentType] = useState(null);

  const onChangeApply = (event, index) => {
    setChecked((prev) => {
      const updated = [...prev];
      updated[index] = event.target.checked;
      return updated;
    });
  };

  const handleCheckAll = (event) => {
    setChecked(fees.map(() => event.target.checked));
  };

  const massEditing = async (event) => {
    event.preventDefault();

    const twoFa = event.target.twofa.value;

    let data = document.getElementsByName("applyTo"), res = [];

    for (let i = 0; i < data.length; i++) {
      if (data[i].checked) {
        res.push(data[i].id);
      }
    }

    axios.post("/api/default-fees/mass-edit", {
        data: res,
        type: currentType,
        value: massEditingData[currentType],
        twoFa
      },
      userAuthenticationConfig()
    ).then(response => {
      if (response.status === 200) {
        fetchDefaultFees();
        setOpen(false);
        setNotification({ ...notification, visible: true, type: "success", message: t("edit.success") });
      }
    }).catch(error => {
      setOpen(false);
      setNotification({ ...notification, visible: true, type: "error", message: error.response.data.detail });
    });

  };

  const changeInputValue = (event, type) => {
    setMassEditingData({ ...massEditingData, [type]: event.target.value });
  };

  const renderApplyButton = (action) => {
    return (
      <DefaultButton color="success" onClick={action}>
        {t("edit.applyTo")} {checked?.filter(Boolean).length}/{fees.length}
      </DefaultButton>
    );
  };

  useEffect(() => {
    setChecked(fees?.map(() => false) || []);
  }, [fees]);

  const renderMassEdit = () => {
    return (
      <StyledFormGrid className="apply-to-wrapper">
        <StyledApplyToWrapper>
          <StyledApplyToItem>
            <TextField
              className="grid-form__input"
              variant="filled"
              label={t("edit.percent")}
              id={inputType.percent}
              type="text"
              value={massEditingData.percent}
              onChange={(event => changeInputValue(event, inputType.percent))}
            />
            {renderApplyButton(() => {
              setCurrentType(inputType.percent);
              setOpen(true);
            })}
          </StyledApplyToItem>
          <StyledApplyToItem>
            <TextField
              className="grid-form__input"
              variant="filled"
              label={t("edit.constant")}
              id={inputType.constant}
              type="text"
              value={massEditingData.constant}
              onChange={(event => changeInputValue(event, inputType.constant))}
            />
            {renderApplyButton(() => {
              setCurrentType(inputType.constant);
              setOpen(true);
            })}
          </StyledApplyToItem>
          <StyledApplyToItem>
            <TextField
              className="grid-form__input"
              variant="filled"
              label={t("edit.min")}
              id={inputType.min}
              type="text"
              value={massEditingData.min}
              onChange={(event => changeInputValue(event, inputType.min))}
            />
            {renderApplyButton(() => {
              setCurrentType(inputType.min);
              setOpen(true);
            })}
          </StyledApplyToItem>
          <StyledApplyToItem>
            <TextField
              className="grid-form__input"
              variant="filled"
              label={t("edit.max")}
              id={inputType.max}
              type="text"
              value={massEditingData.max}
              onChange={(event => changeInputValue(event, inputType.max))}
            />
            {renderApplyButton(() => {
              setCurrentType(inputType.max);
              setOpen(true);
            })}
          </StyledApplyToItem>
        </StyledApplyToWrapper>
      </StyledFormGrid>
    );
  };

  if (!fees) {
    return <PageSpinner
      position="center"
      pt="20"
      pb="20"
    />;
  }

  if (fees.length === 0) {
    return <Alert severity="warning">{t("table.emptyData")}</Alert>;
  }

  return (
    <>
      <DialogModal
        open={open}
        setOpen={setOpen}
        title={t("table.twoFa")}
        content={<TwoFa
          handleSubmit={massEditing}
          error={error}
        />}
      />
      {renderMassEdit()}
      <StyledScrollTable>
        <StyledTable
          width="1480"
          size="14"
        >
          <StyledTableHead
            scroll="auto"
            col="10"
          >
            <div>
              <StyledCheckbox position="center">
                <MaterialCheckbox
                  type="checkbox"
                  name="checkAll"
                  id="checkAll"
                  checked={checked?.length > 0 ? checked.every(Boolean) : false}
                  indeterminate={checked?.length > 0 && checked.some(Boolean) && !checked.every(Boolean)}
                  onChange={handleCheckAll}
                />
              </StyledCheckbox>
            </div>
            <div>
              {t("table.service")}
            </div>
            <div>
              {t("table.paymentSystem")}
            </div>
            <div>
              {t("table.currency")}
            </div>
            <div>
              {t("table.type")}
            </div>
            <div>
              {t("table.percent")}
            </div>
            <div>
              {t("table.constant")}
            </div>
            <div>
              {t("table.min")}
            </div>
            <div>
              {t("table.max")}
            </div>
            <div />
          </StyledTableHead>
          <StyledTableBody>
            {fees && fees.map((value, index) => (
              <StyledTableRow
                key={index}
                scroll="auto"
                col="10"
              >
                <StyledCol>
                  <StyledCheckbox position="center">
                    <MaterialCheckbox
                      type="checkbox"
                      name="applyTo"
                      id={value.id}
                      checked={checked?.[index] || false}
                      onChange={(event) => onChangeApply(event, index)}
                    />
                  </StyledCheckbox>
                </StyledCol>
                <StyledCol>
                  {getServiceNameWithNickname(value.baseFee.service.name, value.baseFee.service.nickname)}
                </StyledCol>
                <StyledCol>
                  {value.baseFee.paymentSystem.name}
                </StyledCol>
                <StyledCol>
                  {value.baseFee.currency.asset}
                </StyledCol>
                <StyledCol>
                  {value.baseFee.type}
                </StyledCol>
                <StyledCol>
                  {Math.round((value.percent * 100) * 1000) / 1000} %
                </StyledCol>
                <StyledCol>
                  {value.constant}
                </StyledCol>
                <StyledCol>
                  {value.min}
                </StyledCol>
                <StyledCol>
                  {value.max}
                </StyledCol>
                <StyledCol inline>
                  <NavLink
                    className="table-action table-action_info"
                    to={"/default-fees/" + value.id}
                  >
                    <span className="icon-light-edit" />
                    {t("table.edit")}
                  </NavLink>
                </StyledCol>
              </StyledTableRow>
            ))}
          </StyledTableBody>
        </StyledTable>
      </StyledScrollTable>
    </>
  );
};
export default DefaultFeeList;